import { useEffect, useState } from 'react';
import type { Document } from 'used-vehicle-contracts';
import { authTokenStore } from './auth-token';
import { apiUrl, endSession, refreshOnce } from './session-refresh';

/**
 * Documents over plain HTTP (plan §2.5, §6).
 *
 * **No token ever appears in a URL.** Not in a query string, not in a signed link, nowhere. A licence
 * scan is fetched with the session's `Authorization` header and handed to the DOM as an object URL, so
 * the address the `<img>` carries is a `blob:` reference that is meaningless outside this tab and dies
 * with it. A URL with a token in it survives in browser history, in the `Referer` header of the next
 * navigation, and in every proxy log between here and the server.
 *
 * Uploads use `XMLHttpRequest` rather than `fetch`: it is still the only browser API that reports upload
 * progress, and a person attaching a 9 MB photograph over a dealership's connection needs a progress bar
 * rather than a spinner that might mean anything.
 *
 * Both paths share the oRPC client's refresh (`session-refresh.ts`), so an upload that starts in the
 * fifteenth minute of a session renews the token and retries instead of failing.
 */

export interface UploadMeta {
  category: string;
  title?: string;
  documentDate?: string;
  notes?: string;
}

export type UploadProgress = (fraction: number) => void;

interface XhrResult {
  status: number;
  body: string;
}

const sendMultipart = (url: string, form: FormData, accessToken: string | null, onProgress?: UploadProgress): Promise<XhrResult> =>
  new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();

    request.open('POST', url);

    if (accessToken) {
      request.setRequestHeader('authorization', `Bearer ${accessToken}`);
    }

    if (onProgress) {
      request.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && event.total > 0) {
          onProgress(event.loaded / event.total);
        }
      });
    }

    request.addEventListener('load', () => resolve({ status: request.status, body: request.responseText }));
    // A network failure has no status; `0` never matches the 401 branch, so it surfaces as an error the
    // Retry button can act on rather than as a spurious sign-out.
    request.addEventListener('error', () => reject(new DocumentHttpError(0, 'Network error')));
    request.addEventListener('abort', () => reject(new DocumentHttpError(0, 'Upload cancelled')));

    request.send(form);
  });

/** Carries the service's own `message` and `reasonKey`, so the UI can print what the server said. */
export class DocumentHttpError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly reasonKey: string | null = null,
  ) {
    super(message);
    this.name = 'DocumentHttpError';
  }
}

const parseError = (status: number, body: string): DocumentHttpError => {
  try {
    const parsed = JSON.parse(body) as { message?: unknown; reasonKey?: unknown };
    const message = typeof parsed.message === 'string' ? parsed.message : `Request failed (${status})`;
    const reasonKey = typeof parsed.reasonKey === 'string' ? parsed.reasonKey : null;

    return new DocumentHttpError(status, message, reasonKey);
  } catch {
    return new DocumentHttpError(status, `Request failed (${status})`);
  }
};

/** Runs a multipart request, renewing the session once if the access token had just expired. */
const uploadWithRefresh = async (url: string, build: () => FormData, onProgress?: UploadProgress): Promise<Document> => {
  // The `FormData` is rebuilt for the retry: a `File` can be read twice, but the request that consumed
  // the first body cannot be replayed.
  const first = await sendMultipart(url, build(), authTokenStore.getAccessToken(), onProgress);

  const result = await (async (): Promise<XhrResult> => {
    if (first.status !== 401) {
      return first;
    }

    const session = await refreshOnce();

    if (!session) {
      endSession();

      return first;
    }

    const retried = await sendMultipart(url, build(), session.accessToken, onProgress);

    if (retried.status === 401) {
      endSession();
    }

    return retried;
  })();

  if (result.status < 200 || result.status >= 300) {
    throw parseError(result.status, result.body);
  }

  return JSON.parse(result.body) as Document;
};

export const uploadDocument = async (vehicleId: string, file: File, meta: UploadMeta, onProgress?: UploadProgress): Promise<Document> =>
  uploadWithRefresh(
    apiUrl(`/vehicles/${vehicleId}/documents`),
    () => {
      const form = new FormData();

      form.append('file', file, file.name);
      form.append('category', meta.category);

      if (meta.title) {
        form.append('title', meta.title);
      }

      if (meta.documentDate) {
        form.append('documentDate', meta.documentDate);
      }

      if (meta.notes) {
        form.append('notes', meta.notes);
      }

      return form;
    },
    onProgress,
  );

export const replaceDocument = async (documentId: string, file: File, onProgress?: UploadProgress): Promise<Document> =>
  uploadWithRefresh(
    apiUrl(`/documents/${documentId}/replace`),
    () => {
      const form = new FormData();

      form.append('file', file, file.name);

      return form;
    },
    onProgress,
  );

/** The bytes, as a `Blob`. Authenticated `fetch`, refreshed once on 401, never a token in the URL. */
export const fetchDocumentBlob = async (documentId: string): Promise<Blob> => {
  const url = apiUrl(`/documents/${documentId}/content`);

  const send = (accessToken: string | null): Promise<Response> =>
    fetch(url, { headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {} });

  let response = await send(authTokenStore.getAccessToken());

  if (response.status === 401) {
    const session = await refreshOnce();

    if (!session) {
      endSession();
    } else {
      response = await send(session.accessToken);

      if (response.status === 401) {
        endSession();
      }
    }
  }

  if (!response.ok) {
    throw parseError(response.status, await response.text());
  }

  return response.blob();
};

export interface DocumentObjectUrl {
  url: string | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * An object URL for one document, revoked on unmount and whenever the id changes.
 *
 * The revoke is the part that is easy to leave out and expensive to leave out: every `createObjectURL`
 * pins its `Blob` in memory until it is released, so a documents tab that is opened and closed twenty
 * times would hold twenty copies of every photograph for the life of the tab.
 */
export const useDocumentObjectUrl = (documentId: string | null): DocumentObjectUrl => {
  const [state, setState] = useState<DocumentObjectUrl>({ url: null, isLoading: documentId !== null, error: null });

  useEffect(() => {
    if (documentId === null) {
      setState({ url: null, isLoading: false, error: null });

      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;

    setState({ url: null, isLoading: true, error: null });

    fetchDocumentBlob(documentId)
      .then((blob) => {
        if (cancelled) {
          return;
        }

        objectUrl = URL.createObjectURL(blob);
        setState({ url: objectUrl, isLoading: false, error: null });
      })
      .catch((caught: unknown) => {
        if (cancelled) {
          return;
        }

        setState({ url: null, isLoading: false, error: caught instanceof Error ? caught.message : String(caught) });
      });

    return () => {
      cancelled = true;

      if (objectUrl !== null) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [documentId]);

  return state;
};

/**
 * Downloads a document through a temporary anchor over its object URL.
 *
 * `<a href="/api/documents/…/content" download>` would be a plain browser navigation with no
 * `Authorization` header, so it would 401. The blob is already in memory for the preview; the anchor
 * simply gives it a file name, and the object URL is revoked immediately afterwards.
 */
export const downloadDocument = async (documentId: string, fileName: string): Promise<void> => {
  const blob = await fetchDocumentBlob(documentId);
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();

  // A short delay: Chrome starts the download asynchronously after the click.
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
};
